import { CompetencyMapQuery } from "@generated/graphql";
import { BoltOutlined } from "@mui/icons-material";
import { Card, Stack, Typography } from "@mui/material";
import DownloadFileButton from "components/buttons/DownloadFileButton";
import { DATE_FORMAT } from "constants/dateFormats";
import dayjs from "dayjs";
import { getWordDeclension } from "helpers";
import { palette } from "theme";
import { getExtensionLabel, getExtensionStyles } from "./utils";

type DocumentsTabProps = {
  files: NonNullable<CompetencyMapQuery["competencyMap"]>["files"];
};

const DocumentsTab = ({ files }: DocumentsTabProps) => {
  const documents = files || [];

  return (
    <Stack spacing={1}>
      <Card>
        <Stack
          direction='row'
          alignItems='flex-start'
          spacing={2}>
          <Stack
            alignItems='center'
            justifyContent='center'
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              flexShrink: 0,
              backgroundColor: palette.primary.main,
            }}>
            <BoltOutlined sx={{ color: palette.white }} />
          </Stack>
          <Stack spacing={0.5}>
            <Typography
              fontWeight={700}
              fontSize={20}>
              Карта сгенерирована на основе этих документов
            </Typography>
            <Typography color='text.secondary'>
              Структура компетенций, вопросы и критерии оценки построены из содержимого загруженных файлов. Документы
              хранятся как источник карты.
            </Typography>
          </Stack>
        </Stack>
      </Card>
      <Card>
        <Stack
          direction='row'
          alignItems='center'
          justifyContent='space-between'
          sx={{ pb: 2, borderBottom: "1px solid", borderColor: "divider" }}>
          <Typography
            fontWeight={700}
            fontSize={20}>
            Файлы
          </Typography>
          <Typography
            variant='body2'
            fontWeight={600}
            sx={{
              px: 1.25,
              py: 0.5,
              borderRadius: 10,
              backgroundColor: "#F6F8FC",
              color: "text.secondary",
            }}>
            {documents.length} {getWordDeclension(documents.length, "document")}
          </Typography>
        </Stack>
        {documents.map((item, index) => {
          const extensionLabel = getExtensionLabel(item.file.extension);
          const extensionStyles = getExtensionStyles(item.file.extension);

          return (
            <Stack
              key={item.id}
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ xs: "flex-start", sm: "center" }}
              justifyContent='space-between'
              spacing={2}
              sx={{
                pt: 1.75,
                borderBottom: index === documents.length - 1 ? "none" : "1px solid",
                borderColor: "divider",
              }}>
              <Stack
                direction='row'
                spacing={1.5}
                alignItems='center'
                sx={{ minWidth: 0 }}>
                <Stack
                  alignItems='center'
                  justifyContent='center'
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    flexShrink: 0,
                    ...extensionStyles,
                  }}>
                  <Typography
                    fontSize={11}
                    fontWeight={800}>
                    {extensionLabel}
                  </Typography>
                </Stack>
                <Stack
                  spacing={0.25}
                  sx={{ minWidth: 0 }}>
                  <Typography
                    fontWeight={600}
                    sx={{ wordBreak: "break-word" }}>
                    {item.file.name}
                  </Typography>
                  <Typography color='text.secondary'>Загружен {dayjs(item.createdAt).format(DATE_FORMAT)}</Typography>
                </Stack>
              </Stack>
              <DownloadFileButton
                href={item.file.url}
                target='_blank'
                tooltipText='Скачать документ'
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                }}
              />
            </Stack>
          );
        })}
      </Card>
    </Stack>
  );
};

export default DocumentsTab;
